import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { useMotionValue } from 'framer-motion';
import { measureChatTeaser3Layout } from './chatteaser3Layout';

type ChatTeaser3LayoutMetrics = ReturnType<typeof measureChatTeaser3Layout>;

export function useChatteaser3LayoutState({
  displayedTab,
}: {
  displayedTab: number;
}) {
  const [layout, setLayout] = useState<ChatTeaser3LayoutMetrics>({
    soloBottom: 320,
    maxScroll: 0,
    wrapperHeight: 600,
    rects: [],
  });
  const layoutRef = useRef(layout);
  const layoutVersion = useMotionValue(0);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const soloRef = useRef<HTMLDivElement>(null);
  const soloContentRef = useRef<HTMLDivElement>(null);
  const unitedHeaderRef = useRef<HTMLDivElement>(null);
  const unitedScrollerRef = useRef<HTMLDivElement>(null);
  const messagesInnerRef = useRef<HTMLDivElement>(null);

  const publishLayout = useCallback((next: ChatTeaser3LayoutMetrics): void => {
    layoutRef.current = next;
    setLayout(next);
    layoutVersion.set(layoutVersion.get() + 1);
  }, [layoutVersion]);

  const measure = useCallback((): void => {
    publishLayout(measureChatTeaser3Layout({
      wrapper: wrapperRef.current,
      soloContent: soloContentRef.current,
      unitedHeader: unitedHeaderRef.current,
      unitedScroller: unitedScrollerRef.current,
      messagesInner: messagesInnerRef.current,
    }));
  }, [publishLayout]);
  // Measure as soon as the displayed content / variant changes (which happens at
  // the exit→enter swap). Rects are layout-only now (no transform corrupts them),
  // so the enter animation gets the correct rest height immediately — no end snap.
  useLayoutEffect(() => {
    measure();
  }, [displayedTab, measure]);

  useEffect(() => {
    const onResize = (): void => measure();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [measure]);

  return {
    layout,
    layoutRef,
    layoutVersion,
    maxScroll: layout.maxScroll,
    messagesInnerRef,
    soloContentRef,
    soloRef,
    unitedHeaderRef,
    unitedScrollerRef,
    wrapperRef,
  };
}
